<template>
  <NCollapse class="c-contact-selector-colleagues" :default-expanded-names="['colleagues']">
    <NCollapseItem :disabled="mutuallyExclusiveQuickFilters" name="colleagues">
      <template #header>
        <NH6 class="ma-0">
          {{ `${t('canvasUI.CContactSelectorColleagues.colleagues')} (${totalColleagues})` }}
        </NH6>
      </template>
      <RecycleScroller
        v-slot="{ item: colleague }"
        class="c-contact-selector-colleagues__account-scroller"
        :item-size="70"
        :items="orderedColleagues"
        key-field="Id"
      >
        <div class="pb-3">
          <div class="c-contact-selector__colleague-item flex items-center b-rounded-1">
            <div class="c-contact-selector__colleague-item__left">
              <CIcon class="mx-4" :color="themeVars.primary4" icon="user" :size="16" />
            </div>
            <div class="c-contact-selector__colleague-item__content">
              <div
                class="font-600 underline truncate"
                v-html="highlight(colleague.FirstName + ' ' + colleague.LastName, search)"
              />

              <div v-if="colleague.Email" class="c-contact-selector__colleague-item__content__email truncate">
                {{ colleague.Email }}
              </div>
            </div>
            <div class="c-contact-selector__colleague-item__right">
              <CButton circle class="mr-2" :disabled="loading" @click="onColleagueClick(colleague)">
                <CIcon fa-type="fas" :icon="selectedColleagueIds?.includes(colleague.Id) ? 'close' : 'plus'" />
              </CButton>
            </div>
          </div>
        </div>
      </RecycleScroller>
    </NCollapseItem>
  </NCollapse>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { NCollapse, NCollapseItem, NH6 } from 'naive-ui'
import { RecycleScroller } from 'vue-virtual-scroller'
import { orderBy } from 'lodash-es'

import { highlight } from '@lib/util/string'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import useThemeVars from '@lib/composables/useThemeVars'

const props = withDefaults(
  defineProps<{
    mutuallyExclusiveQuickFilters?: boolean
    loading?: boolean
    search: string
    selectedColleagueIds: string[]
    totalColleagues: number
    colleagues: any[]
  }>(),
  {
    mutuallyExclusiveQuickFilters: false,
    loading: false,
    search: '',
    selectedColleagueIds: () => [],
  },
)

const emit = defineEmits(['add-colleague', 'remove-colleague'])

const { t } = useI18n()

const nameIteratee = (i: any) => i?.FirstName?.toLowerCase()
const orderedColleagues = computed(() =>
  orderBy(
    props.colleagues?.filter((c) => !!(String(c.FirstName) + String(c.LastName))), // hide 🥷 users
    [nameIteratee],
    ['asc'],
  ),
)
const onColleagueClick = (colleague: any) => {
  return props.selectedColleagueIds.includes(colleague.Id)
    ? emit('remove-colleague', colleague.Id)
    : emit('add-colleague', colleague.Id)
}

const themeVars = useThemeVars()
</script>

<style scoped>
.c-contact-selector-colleagues__account-scroller {
  overflow-y: auto;
}

:deep(.highlight) {
  background-color: v-bind('themeVars.info');
}

.c-contact-selector__colleague-item {
  height: 58px;
  background: v-bind('themeVars.primary6');
}
.c-contact-selector__colleague-item__left {
  flex: 0 0 48px;
}

.c-contact-selector__colleague-item__content {
  flex: 1;
  min-width: 0;
}

.c-contact-selector__colleague-item__right {
  flex: 0 0 48px;
}

.c-contact-selector__colleague-item__content__email {
  color: v-bind('themeVars.text2');
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
}
</style>
